/**
 * Sentry frontend instrumentation.
 *
 * Imported as the FIRST line of `src/index.js` so error/perf hooks wrap
 * every React render that follows. No-ops cleanly when the DSN env var
 * is missing — local dev / preview pods won't spam a production project.
 *
 * DSN is read from `REACT_APP_SENTRY_DSN` (CRA convention — must start
 * with REACT_APP_ to be inlined into the bundle at build time). Release
 * is read from `window.__BIDORAH_BUILD__` (stamped by scripts/write-
 * version.js on every deploy) so each error event is tied to a specific
 * deploy, matching the backend's release tag of the same build id.
 *
 * Strict PII rules: `sendDefaultPii: false` + `beforeSend` strips users,
 * auth headers, cookies, and any extras shaped like a token/secret. We
 * also drop `ui.click` breadcrumbs (Bidorah pages can show usernames /
 * listing titles in click targets) and any breadcrumb containing
 * `email`, `password`, or `token`.
 */
import * as Sentry from "@sentry/react";

const DSN = process.env.REACT_APP_SENTRY_DSN || "";
const ENV = process.env.REACT_APP_SENTRY_ENV || "production";

function _readBuildId() {
  try {
    if (typeof window !== "undefined" && window.__BIDORAH_BUILD__) {
      return String(window.__BIDORAH_BUILD__);
    }
  } catch (_) { /* ignore */ }
  // Inline cache-buster in index.html persists this to localStorage on
  // every successful /version.json fetch, so it's reliably available
  // even when window.__BIDORAH_BUILD__ wasn't stamped at build time.
  try {
    const v = (typeof localStorage !== "undefined" && localStorage.getItem("bidorah.build")) || null;
    if (v) return v;
  } catch (_) { /* ignore */ }
  return "unknown";
}

function _beforeSend(event) {
  if (!event) return event;
  // Never send user identifiers — emails, names, IDs.
  if (event.user) delete event.user;
  // Scrub sensitive extras (tokens, secrets, passwords, card numbers).
  if (event.extra) {
    for (const key of Object.keys(event.extra)) {
      const low = key.toLowerCase();
      if (
        low.includes("token") ||
        low.includes("secret") ||
        low.includes("password") ||
        low.includes("card") ||
        low.includes("cvc")
      ) {
        delete event.extra[key];
      }
    }
  }
  // Scrub request data (headers, body, querystring may contain PII).
  if (event.request) {
    if (event.request.headers) {
      delete event.request.headers.authorization;
      delete event.request.headers.Authorization;
      delete event.request.headers.cookie;
      delete event.request.headers.Cookie;
    }
    delete event.request.data;
    delete event.request.query_string;
  }
  return event;
}

function _beforeBreadcrumb(breadcrumb) {
  if (!breadcrumb) return breadcrumb;
  // Drop UI click breadcrumbs — Bidorah click targets can include
  // usernames, listing titles, and other potentially-PII strings.
  if (breadcrumb.category === "ui.click" || breadcrumb.category === "ui.input") {
    return null;
  }
  // Drop any breadcrumb whose message shape looks PII-bearing.
  const msg = breadcrumb.message || "";
  if (/email|password|token|cvc|ssn/i.test(msg)) {
    return null;
  }
  return breadcrumb;
}

export function installSentry() {
  if (!DSN) {
    // eslint-disable-next-line no-console
    console.info("Sentry: REACT_APP_SENTRY_DSN not set — error monitoring disabled.");
    return;
  }
  try {
    Sentry.init({
      dsn: DSN,
      environment: ENV,
      release: `bidora-frontend@${_readBuildId()}`,
      sendDefaultPii: false,
      tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_RATE || 0.05),
      replaysSessionSampleRate: 0, // never auto-record sessions (would capture chat / DM PII)
      replaysOnErrorSampleRate: 0,
      beforeSend: _beforeSend,
      beforeBreadcrumb: _beforeBreadcrumb,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Sentry init failed (continuing without monitoring):", e);
  }
}

// Re-export the React-specific helpers our index.js / App.js consume.
export const SentryErrorBoundary = Sentry.ErrorBoundary;
export const sentryReactErrorHandler = Sentry.reactErrorHandler;
export { Sentry };
